import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Settings, Save, Key, Plus, Trash2, Eye, EyeOff, Palette, Globe, Mail, Shield, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const API_KEY_PRESETS = [
  { name: 'STRIPE_SECRET_KEY', label: 'Stripe Secret Key', icon: '💳', desc: 'For payment processing (sk_...)' },
  { name: 'STRIPE_PUBLISHABLE_KEY', label: 'Stripe Publishable Key', icon: '💳', desc: 'Frontend payment form (pk_...)' },
  { name: 'RAZORPAY_KEY_ID', label: 'Razorpay Key ID', icon: '💳', desc: 'Razorpay payment gateway' },
  { name: 'RAZORPAY_KEY_SECRET', label: 'Razorpay Secret', icon: '💳', desc: 'Razorpay secret key' },
  { name: 'RESEND_API_KEY', label: 'Resend API Key', icon: '📧', desc: 'Email delivery service (re_...)' },
  { name: 'SENDGRID_API_KEY', label: 'SendGrid API Key', icon: '📧', desc: 'Email delivery (SG....)' },
  { name: 'OPENAI_API_KEY', label: 'OpenAI API Key', icon: '🤖', desc: 'AI features (sk-...)' },
  { name: 'GOOGLE_CLIENT_ID', label: 'Google OAuth Client ID', icon: '🔐', desc: 'Google sign-in integration' },
  { name: 'GOOGLE_CLIENT_SECRET', label: 'Google OAuth Secret', icon: '🔐', desc: 'Google OAuth secret' },
  { name: 'CUSTOM_KEY', label: 'Custom API Key', icon: '🔑', desc: 'Add your own custom key' },
];

export default function SuperAdminSettings() {
  const [settings, setSettings] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newKey, setNewKey] = useState({ key_name: '', key_value: '' });
  const [showValues, setShowValues] = useState({});
  const [customKeyName, setCustomKeyName] = useState('');

  // eslint-disable-next-line no-unused-vars
  const token = localStorage.getItem('superAdminToken');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/platform/settings`, { headers: getHeaders() });
      setSettings(res.data);
    } catch { toast.error('Failed to load settings'); }
    setLoading(false);
  }, []);

  const fetchApiKeys = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/platform/settings/api-keys`, { headers: getHeaders() });
      setApiKeys(res.data);
    } catch { /* empty */ }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchApiKeys();
  }, [fetchSettings, fetchApiKeys]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { type, updated_at, updated_by, ...payload } = settings;
      await axios.put(`${API}/platform/settings`, payload, { headers: getHeaders() });
      toast.success('Settings saved');
      fetchSettings();
    } catch { toast.error('Failed to save settings'); }
    setSaving(false);
  };

  const saveApiKey = async () => {
    const keyName = newKey.key_name === 'CUSTOM_KEY' ? customKeyName : newKey.key_name;
    if (!keyName || !newKey.key_value) { toast.error('Fill in both fields'); return; }
    try {
      await axios.post(`${API}/platform/settings/api-keys`, { key_name: keyName, key_value: newKey.key_value }, { headers: getHeaders() });
      toast.success(`Key "${keyName}" saved`);
      setNewKey({ key_name: '', key_value: '' });
      setCustomKeyName('');
      fetchApiKeys();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save key'); }
  };

  const deleteApiKey = async (keyName) => {
    if (!window.confirm(`Delete API key "${keyName}"?`)) return;
    try {
      await axios.delete(`${API}/platform/settings/api-keys/${keyName}`, { headers: getHeaders() });
      toast.success('Key deleted');
      fetchApiKeys();
    } catch { toast.error('Failed to delete key'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" /></div>;

  return (
    <div className="space-y-8 max-w-5xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Platform Settings</h1>
        <p className="text-slate-400 mt-1">Configure your platform, API keys, and preferences</p>
      </div>

      {/* General Settings */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-4">
          <CardTitle className="text-white flex items-center gap-2"><Globe size={18} />General Configuration</CardTitle>
          <CardDescription className="text-slate-400">Platform identity and defaults</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-300">Platform Name</Label>
              <Input data-testid="setting-platform-name" value={settings?.platform_name || ''} onChange={e => setSettings(s => ({ ...s, platform_name: e.target.value }))} className="mt-1 bg-slate-900 border-slate-600 text-white" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-300">Support Email</Label>
              <Input data-testid="setting-support-email" value={settings?.support_email || ''} onChange={e => setSettings(s => ({ ...s, support_email: e.target.value }))} className="mt-1 bg-slate-900 border-slate-600 text-white" placeholder="support@recruitizr.com" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-300">Default Currency</Label>
              <Select value={settings?.default_currency || 'INR'} onValueChange={v => setSettings(s => ({ ...s, default_currency: v }))}>
                <SelectTrigger className="mt-1 bg-slate-900 border-slate-600 text-white" data-testid="setting-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-white">
                  <SelectItem value="INR" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white data-[highlighted]:bg-slate-700 data-[highlighted]:text-white">INR (₹)</SelectItem>
                  <SelectItem value="USD" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white data-[highlighted]:bg-slate-700 data-[highlighted]:text-white">USD ($)</SelectItem>
                  <SelectItem value="EUR" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white data-[highlighted]:bg-slate-700 data-[highlighted]:text-white">EUR (€)</SelectItem>
                  <SelectItem value="GBP" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white data-[highlighted]:bg-slate-700 data-[highlighted]:text-white">GBP (£)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-300">Trial Period (days)</Label>
              <Input data-testid="setting-trial-days" type="number" value={settings?.trial_days || 14} onChange={e => setSettings(s => ({ ...s, trial_days: parseInt(e.target.value) || 14 }))} className="mt-1 bg-slate-900 border-slate-600 text-white" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-300">Primary Color</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={settings?.primary_color || '#6366f1'} onChange={e => setSettings(s => ({ ...s, primary_color: e.target.value }))} className="h-9 w-12 rounded border border-slate-600 cursor-pointer bg-transparent" />
                <Input value={settings?.primary_color || '#6366f1'} onChange={e => setSettings(s => ({ ...s, primary_color: e.target.value }))} className="bg-slate-900 border-slate-600 text-white" />
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-900 rounded border border-slate-600">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-300">Maintenance Mode</Label>
                <p className="text-xs text-slate-500 mt-0.5">Disables tenant access when enabled</p>
              </div>
              <Switch checked={settings?.maintenance_mode || false} onCheckedChange={v => setSettings(s => ({ ...s, maintenance_mode: v }))} data-testid="setting-maintenance" />
            </div>
          </div>
          <Button onClick={saveSettings} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white font-bold" data-testid="save-settings-btn">
            <Save size={14} className="mr-2" />{saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </CardContent>
      </Card>

      {/* API Keys Management */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-4">
          <CardTitle className="text-white flex items-center gap-2"><Key size={18} />API Keys & Integrations</CardTitle>
          <CardDescription className="text-slate-400">Manage credentials for third-party services. Keys are encrypted at rest.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Existing Keys */}
          {apiKeys.length > 0 && (
            <div className="space-y-2">
              {apiKeys.map(k => {
                const preset = API_KEY_PRESETS.find(p => p.name === k.key_name);
                return (
                  <div key={k.key_name} className="flex items-center justify-between p-3 bg-slate-900 rounded border border-slate-600" data-testid={`api-key-${k.key_name}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{preset?.icon || '🔑'}</span>
                      <div>
                        <p className="text-sm font-semibold text-white">{k.key_name}</p>
                        <p className="text-xs text-slate-400">{showValues[k.key_name] ? k.key_value_masked : '••••••••'} &middot; Updated {k.updated_at?.slice(0, 10)} by {k.updated_by}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white h-8 w-8 p-0" onClick={() => setShowValues(v => ({ ...v, [k.key_name]: !v[k.key_name] }))}>
                        {showValues[k.key_name] ? <EyeOff size={14} /> : <Eye size={14} />}
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-8 w-8 p-0" onClick={() => deleteApiKey(k.key_name)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add New Key */}
          <div className="p-4 bg-slate-900/50 rounded border border-dashed border-slate-600">
            <p className="text-sm font-bold text-slate-300 mb-3 flex items-center gap-2"><Plus size={14} />Add API Key</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Select value={newKey.key_name} onValueChange={v => setNewKey(prev => ({ ...prev, key_name: v }))}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="new-key-select">
                  <SelectValue placeholder="Select key type..." />
                </SelectTrigger>
                <SelectContent>
                  {API_KEY_PRESETS.map(p => (
                    <SelectItem key={p.name} value={p.name}>
                      <span className="flex items-center gap-2">{p.icon} {p.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {newKey.key_name === 'CUSTOM_KEY' && (
                <Input placeholder="Custom key name..." value={customKeyName} onChange={e => setCustomKeyName(e.target.value)} className="bg-slate-800 border-slate-600 text-white" data-testid="custom-key-name" />
              )}
              <Input type="password" placeholder="Paste API key value..." value={newKey.key_value} onChange={e => setNewKey(prev => ({ ...prev, key_value: e.target.value }))} className="bg-slate-800 border-slate-600 text-white" data-testid="new-key-value" />
              <Button onClick={saveApiKey} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="save-key-btn">
                <Save size={14} className="mr-2" />Save Key
              </Button>
            </div>
            {newKey.key_name && newKey.key_name !== 'CUSTOM_KEY' && (
              <p className="text-xs text-slate-500 mt-2">{API_KEY_PRESETS.find(p => p.name === newKey.key_name)?.desc}</p>
            )}
          </div>

          <div className="p-3 bg-amber-900/20 border border-amber-700/30 rounded flex items-start gap-2">
            <AlertCircle size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-300">API keys are stored securely in the platform database. For production, consider using a dedicated secrets manager. Keys added here are used by the platform backend for integrations.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
